import React from 'react';
import { Formik, Form, Field } from 'formik';
import { TFunction } from 'i18next';
import { Typography, IconButton, InputAdornment } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Link } from 'react-router-dom';
import { TextField } from 'formik-material-ui';
import classNames from 'classnames';
import { Route } from '~/Constants/Routes.constants';
import { validateEmail } from '../../../Helpers/Validators.helper';
import { usePasswordToggler } from '../../../Hooks/usePasswordHidden';
import { Button } from '~/UIKit';
import { PRIMARY_BLUE } from '~/Constants/Colors.constants';
import { makeStyles } from '~/UIKit/Style';
import { Error } from '~/Components';
import { IErrorResponse } from '~/Models/DTO/Error.models';

const { RECOVER_PASSWORD, LOGIN_USC } = Route;

export interface LoginFormState {
  email: string;
  password: string;
}

const INITIAL_VALUES: LoginFormState = {
  email: '',
  password: '',
};

export interface LoginFormProps {
  onSubmit: (values: LoginFormState) => void;
  t: TFunction;
  isLoading: boolean;
  hasSignUp?: boolean;
  isUsingThePartnerTool?: boolean;
  error?: IErrorResponse;
}

const useStyles = makeStyles(() => ({
  form: {
    maxWidth: 375,
    width: '100%',
    margin: 'auto',
  },
  recoverPasswordLink: {
    float: 'right',
    marginTop: 15,
    marginBottom: 30,
  },
  link: {
    color: PRIMARY_BLUE,
    textDecoration: 'none',
  },
  createNewAccountText: {
    margin: '30px 0',
  },
  signInText: {
    textAlign: 'center',
    fontWeight: 900,
    marginBottom: 30,
  },
}));

export const LoginForm = ({
  onSubmit,
  t,
  isLoading,
  hasSignUp = true,
  isUsingThePartnerTool,
  error,
}: LoginFormProps) => {
  const { onShowPasswordClick, isPasswordVisible, passwordInputType } = usePasswordToggler();
  const { classes } = useStyles();
  return (
    <Formik initialValues={INITIAL_VALUES} onSubmit={onSubmit}>
      {() => (
        <Form className={classes.form}>
          <Typography className={classes.signInText} id="sign-in-text" variant="h5">
            {t('login.signIn')}
          </Typography>
          <Field
            component={TextField}
            fullWidth
            id="name"
            label={t('app.email')}
            margin="dense"
            name="email"
            type="email"
            validate={validateEmail}
            disabled={isLoading}
          />
          <Field
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    edge="end"
                    onClick={onShowPasswordClick}
                    size="large"
                  >
                    {isPasswordVisible ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            component={TextField}
            fullWidth
            id="password"
            label={t('app.password')}
            margin="dense"
            name="password"
            placeholder="Password"
            type={passwordInputType}
            data-testid="login-password-input"
            disabled={isLoading}
          />
          <Error error={error} />
          <Link
            className={classNames(classes.link, classes.recoverPasswordLink)}
            to={`${RECOVER_PASSWORD}${isUsingThePartnerTool ? `?returnUrl=${LOGIN_USC}` : ''}`}
          >
            {t('login.recoverPassword')}
          </Link>
          <Button
            color="primary"
            disabled={isLoading}
            fullWidth
            type="submit"
            variant="contained"
            data-testid="login.signInButton"
          >
            {t('login.signIn')}
          </Button>
          {hasSignUp && (
            <Typography align="center" className={classes.createNewAccountText} variant="body2">
              <div style={{ display: 'grid', justifyContent: 'center' }}>
                <span>{t('login.createNewAccount')}</span>
                <span>{t('login.signupNow')}</span>
              </div>
            </Typography>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default LoginForm;
